// --- START OF FILE KickoffSprintView.js ---
/* frontend/src/components/KickoffSprintView.js */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Maximize2, Minimize2 } from "lucide-react";
import { apiService } from "../services/api";
import ObjectiveItem from "./ObjectiveItem"; // Assurez-vous que ce chemin est correct
import { useObjectiveRewrites } from "./Muse/MuseRewrite";

// --- Offsets pour essayer de centrer l'élément sous le curseur ---
const TASK_ITEM_OFFSET_X = 90;
const TASK_ITEM_OFFSET_Y = 90;

const INPUT_OFFSET_X = 96;
const INPUT_OFFSET_Y = 20;

const KickoffSprintView = ({
  projectId: propProjectId,
  variant = "standalone",
  refreshToken = 0,
  onKickoffSync,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const projectId = Number(propProjectId || params.projectId);
  const isEmbedded = variant === "panel";

  const [objectives, setObjectives] = useState([]);
  const [inputObjective, setInputObjective] = useState(null);
  const [sprints, setSprints] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [iterations, setIterations] = useState([]);
  const [error, setError] = useState(null);

  const [suggestedTitles, setSuggestedTitles] = useState({});
  const [dismissedSuggestions, setDismissedSuggestions] = useState({});
  const [showAllSprints, setShowAllSprints] = useState(false);
  const objectivesRef = useRef(objectives);
  objectivesRef.current = objectives;
  const museRewrites = useObjectiveRewrites({
    projectId, objectivesRef, setObjectives, onChange: () => onKickoffSync && onKickoffSync("objectives"),
  });
  const workbenchRef = useRef(null);

  const isDateInRange = (date, start, end) => {
    if (!start && !end) return false;
    if (start && date < start) return false;
    if (end && date > end) return false;
    return true;
  };

  const formatDateRange = (startDate, endDate) => {
    let start = startDate ? new Date(startDate) : null;
    let end = endDate ? new Date(endDate) : null;
    if (start && Number.isNaN(start.getTime())) start = null;
    if (end && Number.isNaN(end.getTime())) end = null;
    if (!start && !end) return "";
    const formatOptions = { day: "2-digit", month: "short" };
    const startLabel = start
      ? start.toLocaleDateString("fr-FR", formatOptions)
      : "";
    const endLabel = end ? end.toLocaleDateString("fr-FR", formatOptions) : "";
    if (startLabel && endLabel) return `${startLabel} -> ${endLabel}`;
    return startLabel || endLabel;
  };

  const sortedIterations = useMemo(() => {
    return [...iterations].sort(
      (a, b) => new Date(a.start_date) - new Date(b.start_date),
    );
  }, [iterations]);

  const sortedSprints = useMemo(() => {
    return [...sprints].sort(
      (a, b) =>
        new Date(a.start_date) - new Date(b.start_date) || a.id - b.id,
    );
  }, [sprints]);

  const currentSprint = useMemo(() => {
    const now = new Date();
    const active = sortedSprints.filter((sprint) => {
      const start = sprint.start_date ? new Date(sprint.start_date) : null;
      const end = sprint.end_date ? new Date(sprint.end_date) : null;
      if ((start && Number.isNaN(start.getTime())) || (end && Number.isNaN(end.getTime()))) {
        return false;
      }
      return isDateInRange(now, start, end);
    });
    if (active.length === 0) return null;
    return active.sort(
      (a, b) => new Date(b.start_date) - new Date(a.start_date),
    )[0];
  }, [sortedSprints]);

  const currentIteration = useMemo(() => {
    const now = new Date();
    const active = sortedIterations.filter((iteration) => {
      const start = iteration.start_date ? new Date(iteration.start_date) : null;
      const end = iteration.end_date ? new Date(iteration.end_date) : null;
      if ((start && Number.isNaN(start.getTime())) || (end && Number.isNaN(end.getTime()))) {
        return false;
      }
      return isDateInRange(now, start, end);
    });
    if (active.length === 0) return null;
    return active.sort(
      (a, b) => new Date(b.start_date) - new Date(a.start_date),
    )[0];
  }, [sortedIterations]);

  const isSprintCurrent = (sprint) => {
    if (!sprint) return false;
    if (currentSprint?.id && sprint.id === currentSprint.id) return true;
    const start = sprint.start_date ? new Date(sprint.start_date) : null;
    const end = sprint.end_date ? new Date(sprint.end_date) : null;
    if ((start && Number.isNaN(start.getTime())) || (end && Number.isNaN(end.getTime()))) {
      return false;
    }
    return isDateInRange(new Date(), start, end);
  };

  const isIterationCurrent = (iteration) => {
    if (!iteration) return false;
    if (currentIteration?.id && iteration.id === currentIteration.id) return true;
    const start = iteration.start_date ? new Date(iteration.start_date) : null;
    const end = iteration.end_date ? new Date(iteration.end_date) : null;
    if ((start && Number.isNaN(start.getTime())) || (end && Number.isNaN(end.getTime()))) {
      return false;
    }
    return isDateInRange(new Date(), start, end);
  };

  const getRelativePosition = (event, offsetX, offsetY) => {
    const container = workbenchRef.current;
    if (!container) {
      return { x: "0%", y: "0%" };
    }
    const rect = container.getBoundingClientRect();
    const rawX = event.clientX - rect.left - offsetX;
    const rawY = event.clientY - rect.top - offsetY;
    const clampedX = Math.max(0, Math.min(rawX, rect.width));
    const clampedY = Math.max(0, Math.min(rawY, rect.height));
    const percentX = rect.width ? (clampedX / rect.width) * 100 : 0;
    const percentY = rect.height ? (clampedY / rect.height) * 100 : 0;
    return { x: `${percentX.toFixed(2)}%`, y: `${percentY.toFixed(2)}%` };
  };
    
    // --- NOUVEAU : Mettre la logique de fetch dans une fonction réutilisable ---
    const fetchObjectives = useCallback(() => {
        if (!projectId || isNaN(projectId)) return;
        setError(null);
        apiService.get(`/projects/${projectId}/objectives`)
            .then((data) => {
                const transformed = data
                    .sort((a, b) => a.id - b.id)
                    .map(objective => ({
                        ...objective,
                        // Conserver les positions si elles existent déjà
                        x: objective.sprint_id ? null : (objective.x ?? `${Math.floor(Math.random() * 80 + 10)}%`),
                        y: objective.sprint_id ? null : (objective.y ?? `${Math.floor(Math.random() * 80 + 10)}%`),
                    }));
                setObjectives(transformed);
            })
            .catch(err => { console.error("Error fetching objectives:", err); setError("Erreur chargement objectifs."); });
    }, [projectId]);

    // Lancer le fetch initial
    useEffect(() => {
        fetchObjectives();
    }, [fetchObjectives, refreshToken]);

  // Fetch related data
  useEffect(() => {
    if (!projectId || isNaN(projectId)) return;
    setError(null);
    const fetchRelatedData = async () => {
        try {
            const [iterationsData, employeesData, sprintsData] = await Promise.all([
                apiService.get(`/projects/${projectId}/iterations`),
                apiService.get("/employees"),
                apiService.get(`/projects/${projectId}/sprints`)
            ]);
            setIterations(iterationsData);
            setEmployees(employeesData);
            setSprints(sprintsData);
        } catch (err) {
            console.error("Error fetching related data:", err);
            setError("Erreur chargement des données de support.");
        }
    };
    fetchRelatedData();
  }, [projectId]);

  const displaySprints = useMemo(() => {
    if (showAllSprints) return sortedSprints;
    if (sortedSprints.length <= 4) return sortedSprints;
    if (!currentSprint) return sortedSprints.slice(-4);
    const currentIndex = sortedSprints.findIndex(
      (sprint) => sprint.id === currentSprint.id,
    );
    if (currentIndex === -1) return sortedSprints.slice(-4);
    let startIndex = currentIndex - 1;
    if (startIndex < 0) startIndex = 0;
    const maxStart = sortedSprints.length - 4;
    if (startIndex > maxStart) startIndex = maxStart;
    return sortedSprints.slice(startIndex, startIndex + 4);
  }, [showAllSprints, sortedSprints, currentSprint]);

  const sprintsByIteration = useMemo(() => {
    const map = new Map();
    sortedSprints.forEach((sprint) => {
      if (!sprint?.iteration_id) return;
      const key = String(sprint.iteration_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(sprint);
    });
    return map;
  }, [sortedSprints]);
  
  // --- NOUVEAU: Handlers déplacés ici ---
  const handleAcceptSuggestion = (objectiveId, newTitle) => {
    apiService.patch(`/projects/${projectId}/objectives/${objectiveId}`, { name: newTitle })
      .then(updatedObjective => {
        setObjectives(prev => prev.map(t => t.id === objectiveId ? { ...t, name: updatedObjective.name } : t));
        setSuggestedTitles(prev => {
          const newSuggestions = { ...prev };
          delete newSuggestions[objectiveId];
          return newSuggestions;
        });
        if (onKickoffSync) onKickoffSync("objectives");
      })
      .catch(err => {
        console.error("Erreur mise à jour du titre:", err);
        setError("Erreur mise à jour du titre.");
      });
  };

  const handleDeclineSuggestion = (objectiveId) => {
    setDismissedSuggestions(prev => ({ ...prev, [objectiveId]: true }));
  };

  const handleRequestSuggestion = async (objectiveId, objectiveName) => {
    try {
        await museRewrites.rewrite({ id: objectiveId, name: objectiveName });
    } catch (error) {
        console.error("Erreur lors de la demande de suggestion de titre:", error);
        setError("Erreur de suggestion de titre IA.");
    }
  };

  const renderObjectiveCard = useCallback(
    (objective, style = {}) => (
      <div
        key={objective.id}
        style={(!objective.sprint_id && objective.x != null && objective.y != null) ? { ...style, position: "absolute", left: objective.x, top: objective.y, cursor: 'grab' } : { cursor: 'grab' }}
        draggable={true}
        onDragStart={(e) => { e.dataTransfer.setData("text/plain", objective.id); }}
        className="objective-item-container z-10"
      >
        <ObjectiveItem
          objective={objective}
          onDelete={(id) => {
            const objectiveProjectId = objective.project_id || projectId;
            apiService
              .delete(`/projects/${objectiveProjectId}/objectives/${id}`)
              .then(() => {
                setObjectives(prev => prev.filter(t => t.id !== id));
                if (onKickoffSync) onKickoffSync("objectives");
              })
              .catch(err => { console.error("Error deleting objective:", err); setError("Erreur suppression objectif."); });
          }}
          onUpdate={(updatedFields) => {
            const objectiveProjectId = objective.project_id || projectId;
            const { x, y, ...fieldsToSend } = updatedFields;
            if (Object.keys(fieldsToSend).length > 0) {
              apiService
                .patch(`/projects/${objectiveProjectId}/objectives/${objective.id}`, fieldsToSend)
                .then(response => {
                  setObjectives(prev => prev.map(t => t.id === objective.id ? { ...t, ...response, x: t.x, y: t.y } : t));
                  if (onKickoffSync) onKickoffSync("objectives");
                })
                .catch(err => { console.error("Erreur de mise a jour :", err); setError("Erreur mise a jour objectif."); });
            }
          }}
          sprintOptions={sprints}
          ownerOptions={employees}
          showPredictedTokenUI={true}
          predictedTokens={objective.predicted_tokens}
          onPredictedTokenClick={(e) => {
            e.stopPropagation();
            if (objective.predicted_tokens != null) {
              apiService
                .patch(`/projects/${objective.project_id}/objectives/${objective.id}`, { effort: objective.predicted_tokens })
                .then(response => {
                  setObjectives(prev => prev.map(t => t.id === objective.id ? { ...t, ...response } : t));
                  if (onKickoffSync) onKickoffSync("objectives");
                })
                .catch(err => { console.error("Erreur mise a jour effort:", err); setError("Erreur mise a jour effort."); });
            }
          }}
          suggestedTitle={suggestedTitles[objective.id]}
          isSuggestionDismissed={dismissedSuggestions[objective.id]}
          onAcceptSuggestion={handleAcceptSuggestion}
          onDeclineSuggestion={handleDeclineSuggestion}
          onRequestSuggestion={handleRequestSuggestion} // MODIF: Passer le handler
          museRewrite={museRewrites.rewrites[objective.id]}
          museThinking={!!museRewrites.thinking[objective.id]}
          onUndoRewrite={museRewrites.undo}
          onRewriteSettled={museRewrites.settle}
          onRefresh={fetchObjectives} // --- NOUVEAU : Passer la fonction de refresh ---
        />
      </div>
    ),
    [projectId, sprints, employees, objectives, suggestedTitles, dismissedSuggestions, fetchObjectives, onKickoffSync, museRewrites]
  );
    

  const handleDropOnSprint = async (e, sprintId) => {
    e.preventDefault();
    const objectiveId = e.dataTransfer.getData("text/plain");
    const droppedObjective = objectives.find(objective => objective.id.toString() === objectiveId);

    if (!droppedObjective || droppedObjective.sprint_id === sprintId) return;

    const objectiveProjectId = droppedObjective.project_id || projectId;
    setObjectives(prev => prev.map(t => t.id.toString() === objectiveId ? { ...t, sprint_id: sprintId, x: null, y: null } : t));

    try {
        await apiService.patch(`/projects/${objectiveProjectId}/objectives/${objectiveId}`, { sprint_id: sprintId, x: null, y: null });
        if (onKickoffSync) onKickoffSync("objectives");
    } catch (error) {
        console.error("Erreur affectation sprint:", error);
        setError("Erreur affectation sprint.");
        setObjectives(prev => prev.map(t => t.id.toString() === objectiveId ? droppedObjective : t));
    }
  };

  const handleDropNonAssigned = async (e) => {
    e.preventDefault();
    const objectiveId = e.dataTransfer.getData("text/plain");
    const droppedObjective = objectives.find(objective => objective.id.toString() === objectiveId);

    if (!droppedObjective) return;

    const newPosition = getRelativePosition(e, TASK_ITEM_OFFSET_X, TASK_ITEM_OFFSET_Y);

    const cameFromSprint = !!droppedObjective.sprint_id;

    setObjectives(prev => prev.map(t =>
        t.id.toString() === objectiveId
            ? { ...t, sprint_id: null, ...newPosition }
            : t
    ));

    const objectiveProjectId = droppedObjective.project_id || projectId;
    try {
        const payload = { x: newPosition.x, y: newPosition.y };
        if (cameFromSprint) {
            payload.sprint_id = null;
        }
        await apiService.patch(`/projects/${objectiveProjectId}/objectives/${objectiveId}`, payload);
        if (onKickoffSync) onKickoffSync("objectives");
    } catch (error) {
        console.error("Erreur desaffectation sprint (backend):", error);
        setError("Erreur desaffectation sprint.");
        setObjectives(prev => prev.map(t => t.id.toString() === objectiveId ? droppedObjective : t));
    }
  };

  // --- Création de Objectif ---
  const handleBottomClick = (e) => {
       if (e.target.closest('.objective-item-container') || e.target.tagName === 'INPUT') return;
       const newObjectiveId = `new-${Date.now()}`;
       const newPosition = getRelativePosition(e, INPUT_OFFSET_X, INPUT_OFFSET_Y);
       setInputObjective({ id: newObjectiveId, x: newPosition.x, y: newPosition.y, value: "" });
  };
  
  const handleInputBlur = () => {
    if (inputObjective && inputObjective.value.trim() !== "") {
      if (!projectId || isNaN(projectId)) { setError("Veuillez selectionner un projet valide."); setInputObjective(null); return; }

      const newObjectivePayload = { project_id: projectId, name: inputObjective.value.trim(), description: "", sprint_id: null, effort: 0, owner_id: null, status: "not_started", x: inputObjective.x, y: inputObjective.y };
      const tempId = -Date.now();
      const tempObjective = { id: tempId, ...newObjectivePayload, predicted_tokens: null };

      setObjectives(prev => [...prev, tempObjective].sort((a,b) => a.id - b.id));

      apiService.post(`/projects/${projectId}/objectives`, newObjectivePayload)
        .then(createdObjective => {
          setObjectives(prev => {
            const next = prev.map(t => t.id === tempId ? { ...t, ...createdObjective, x: createdObjective.x ?? t.x, y: createdObjective.y ?? t.y } : t);
            return next.slice().sort((a,b) => a.id - b.id);
          });
          if (onKickoffSync) onKickoffSync("objectives");

          museRewrites.rewrite(createdObjective); // Muse relit le titre et l'impose s'il le reformule
        })
        .catch(error => {
          console.error("Erreur creation objectif :", error);
          setError("Erreur creation objectif.");
          setObjectives(prev => prev.filter(t => t.id !== tempId));
        });
    }
    setInputObjective(null);
  };

const handleInputChange = (e) => setInputObjective({ ...inputObjective, value: e.target.value });
  const handleInputKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); handleInputBlur(); } else if (e.key === "Escape") { e.preventDefault(); setInputObjective(null); } };
  
  const objectivesUnassigned = useMemo(() => objectives.filter(objective => !objective.sprint_id && objective.status !== "done").sort((a, b) => a.id - b.id), [objectives]);

  const [workbenchExpanded, setWorkbenchExpanded] = useState(false);

  const containerClass = isEmbedded
    ? "flex flex-col gap-4"
    : "h-screen flex flex-col bg-gray-100";
  const toolbarClass = isEmbedded
    ? "flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 shadow-sm"
    : "flex justify-between items-center p-4";
  const lanesWrapperClass = isEmbedded
    ? "relative rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm"
    : "flex-shrink-0 h-[45%] border-b border-gray-300 p-4 bg-gray-50";
  const backlogWrapperClass = isEmbedded
    ? "relative rounded-2xl border border-dashed border-slate-300 bg-slate-50/70 p-4"
    : "flex-1 relative bg-gray-50 p-4 border-t border-dashed border-gray-300";
  const roadmapWrapperClass = isEmbedded
    ? "rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm"
    : "border-b border-gray-300 bg-white p-4";
  const sprintSectionStyle = isEmbedded
    ? { height: workbenchExpanded ? "35vh" : "50vh" }
    : undefined;
  const backlogSectionStyle = isEmbedded
    ? { minHeight: workbenchExpanded ? "65vh" : "50vh" }
    : undefined;

  return (
    <div className={containerClass}>
      {error && (<div className={`p-2 bg-red-100 text-red-700 rounded text-center text-sm mb-2 ${isEmbedded ? "" : "mx-4"} flex-shrink-0`} role="alert"> {error} <button onClick={() => setError(null)} className="ml-2 text-red-900 font-bold">×</button> </div>)}

      <div className={isEmbedded ? "flex flex-col gap-4" : "flex flex-col flex-1 min-h-0"}>
        <div className={roadmapWrapperClass}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-slate-800">Roadmap du projet</p>
              <p className="text-xs text-slate-500">Iterations planifiees</p>
            </div>
            {currentIteration && (
              <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                Iteration en cours:{" "}
                {currentIteration.name ||
                  currentIteration.description ||
                  `Iteration ${currentIteration.id}`}
              </span>
            )}
          </div>
          {sortedIterations.length > 0 ? (
            <div className="mt-3 flex gap-3 overflow-x-auto no-scrollbar pb-1">
              {sortedIterations.map((iteration) => {
                const isCurrent = isIterationCurrent(iteration);
                const dateLabel = formatDateRange(
                  iteration.start_date,
                  iteration.end_date,
                );
                const iterationSprints =
                  sprintsByIteration.get(String(iteration.id)) || [];
                return (
                  <div
                    key={iteration.id}
                    className={`min-w-[180px] rounded-xl border px-3 py-2 text-xs shadow-sm transition ${
                      isCurrent
                        ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                        : "border-slate-200 bg-white text-slate-700"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold">
                        {iteration.name ||
                          iteration.description ||
                          `Iteration ${iteration.id}`}
                      </p>
                      {isCurrent && (
                        <span className="rounded-full bg-emerald-200/70 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
                          En cours
                        </span>
                      )}
                    </div>
                    {dateLabel && (
                      <p className="mt-1 text-[11px] text-slate-500">
                        {dateLabel}
                      </p>
                    )}
                    {iteration.description && (
                      <p className="mt-1 truncate text-[11px] text-slate-400" title={iteration.description}>
                        {iteration.description}
                      </p>
                    )}
                    {iterationSprints.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {iterationSprints.map((sprint) => {
                          const sprintLabel =
                            sprint.description ||
                            sprint.name ||
                            `Sprint ${sprint.id}`;
                          const sprintCurrent = isSprintCurrent(sprint);
                          return (
                            <span
                              key={sprint.id}
                              className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                                sprintCurrent
                                  ? "border-emerald-200 bg-emerald-100 text-emerald-800"
                                  : "border-slate-200 bg-slate-50 text-slate-600"
                              }`}
                            >
                              {sprintLabel}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="mt-2 text-[10px] text-slate-400">
                        Aucun sprint
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-3 text-xs text-slate-400 italic">
              Aucune iteration dans la roadmap.
            </p>
          )}
        </div>
        <div className={toolbarClass}>
          <h2 className="text-lg font-semibold">Sprints</h2>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setWorkbenchExpanded((prev) => !prev)}
              className="inline-flex items-center rounded-full border border-slate-200 bg-white/80 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            >
              {workbenchExpanded ? (
                <>
                  <Minimize2 className="mr-2 h-4 w-4" />
                  Réduire le workbench
                </>
              ) : (
                <>
                  <Maximize2 className="mr-2 h-4 w-4" />
                  Agrandir le workbench
                </>
              )}
            </button>
            <button 
              onClick={() => setShowAllSprints(!showAllSprints)}
              className="inline-flex items-center rounded-full border border-blue-200 bg-blue-600/90 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
            >
              {showAllSprints ? "Afficher les 4 derniers" : "Afficher tous les sprints"}
            </button>
          </div>
        </div>
        <div
          className={`${lanesWrapperClass} flex overflow-x-auto no-scrollbar`}
          style={{
            ...sprintSectionStyle,
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          <div className="flex flex-1 gap-4 h-full">
             {displaySprints.length > 0 ? (
               displaySprints.map(s => {
                 const sprintLabel = s.description || s.name || `Sprint ${s.id}`;
                 const sprintDateLabel = formatDateRange(s.start_date, s.end_date);
                 const isCurrent = isSprintCurrent(s);
                 return (
                    <div
                      key={s.id}
                      className={`flex flex-col h-full flex-grow basis-80 min-w-64 rounded-2xl border p-4 shadow-sm transition ${isCurrent ? "border-emerald-200 bg-emerald-50/70" : "border-slate-100 bg-white/80"}`}
                      onDragOver={e => e.preventDefault()}
                      onDrop={(e) => handleDropOnSprint(e, s.id)}
                    >
                       <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold mb-1 flex-shrink-0">{sprintLabel}</h3>
                        {isCurrent && (
                          <span className="rounded-full bg-emerald-200/70 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
                            En cours
                          </span>
                        )}
                       </div>
                       {sprintDateLabel && (
                        <p className="text-[11px] text-slate-500 mb-2 flex-shrink-0">{sprintDateLabel}</p>
                       )}
                       {s.vision && (<p className="text-xs text-gray-500 mb-2 truncate flex-shrink-0" title={s.vision}>{s.vision}</p>)}
                      <div className="flex-1 overflow-y-auto no-scrollbar space-y-1 -mr-1 pr-1" style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
                        {objectives.filter(t => t.sprint_id === s.id && t.status !== "done").sort((a, b) => a.id - b.id).map(objective => renderObjectiveCard(objective, {}))}
                      </div>
                    </div>
                 );
              })
             ) : (<div className="flex h-full flex-grow basis-80 min-w-64 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 text-sm text-slate-400">Aucun sprint a afficher.</div>)}
            {!showAllSprints && displaySprints.length > 0 && displaySprints.length < 4 && (Array.from({ length: 4 - displaySprints.length }).map((_, index) => (<div key={`placeholder-${index}`} className="flex h-full flex-grow basis-80 min-w-64 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 text-sm text-slate-400">Slot disponible</div>)))}
          </div>
        </div>
        
        <div id="unassigned-sprint-objectives-container" ref={workbenchRef} className={`${backlogWrapperClass} overflow-auto no-scrollbar`} onClick={handleBottomClick} onDragOver={e => e.preventDefault()} onDrop={handleDropNonAssigned} style={{ ...backlogSectionStyle, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
            <div className="space-y-1">
              {objectivesUnassigned.map(objective => renderObjectiveCard(objective))}
            </div>
            {inputObjective && (<input autoFocus type="text" value={inputObjective.value} onChange={handleInputChange} onBlur={handleInputBlur} onKeyDown={handleInputKeyDown} style={{ position: "absolute", left: inputObjective.x, top: inputObjective.y }} className="border border-blue-300 rounded p-2 shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400 z-20 w-48" placeholder="Nom de l'objectif"/>)}
            {!inputObjective && objectivesUnassigned.length === 0 && (<div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400 pointer-events-none z-0">Glissez ici pour désassigner ou cliquez pour ajouter un objectif</div>)}
        </div>
      </div>
    </div>
  );
};

export default KickoffSprintView;
// --- END OF FILE KickoffSprintView.js ---
